import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Alert,
  AlertTitle,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Tooltip,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  LocalShipping,
  CheckCircle,
  Pending,
  Cancel,
  Schedule,
  Payment,
  Receipt,
  Info,
  Warning,
  Store,
  Home,
  AccessTime,
  CalendarToday,
  AccountCircle,
  Phone,
  LocationOn,
  ShoppingBag,
  LocalOffer,
  Star,
  Download,
  Print,
  Email,
  WhatsApp,
  TrendingDown,
  Business,
  Verified
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import { orderAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';

const ProductOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to format shipping address
  const formatShippingAddress = (shippingAddress) => {
    if (!shippingAddress) return 'Address not available';
    
    // If it's a string (legacy format), return as is
    if (typeof shippingAddress === 'string') {
      return shippingAddress;
    }
    
    // If it's an object, format the address
    const { street, city, state, postcode, country } = shippingAddress;
    
    if (!street) return 'Address not available';
    
    const addressParts = [street];
    if (city) addressParts.push(city);
    if (state && postcode) addressParts.push(`${state} ${postcode}`);
    if (country && country !== 'Australia') addressParts.push(country);
    
    return addressParts.join(', ');
  };

  // Fetch order data from API
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await orderAPI.getOrderDetails(orderId);
        
        if (response.success) {
          setOrder(response.data.order || response.data);
        } else {
          setOrder(null);
        }
      } catch (error) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#FFA500';
      case 'confirmed': return '#007AFF';
      case 'processing': return '#FF6B35';
      case 'shipped': return '#4CAF50';
      case 'delivered': return '#2E7D32';
      case 'cancelled': return '#D32F2F';
      default: return '#666';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'confirmed': return 'Confirmed';
      case 'processing': return 'Processing';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const handleBack = () => {
    navigate('/product-orders');
  };

  const handleDownloadPDF = () => {
    if (!order) return;
    
    try {
      const doc = new jsPDF();
      
      // Set document properties
      doc.setProperties({
        title: `Order Invoice - ${order.orderNumber}`,
        subject: 'Product Order Invoice',
        author: 'Nistylez Store',
        creator: 'Nistylez Customer App'
      });
      
             // Add header
       doc.setFontSize(20);
       doc.setTextColor(10, 42, 102); // #0A2A66
       doc.text('NISTYLEZ STORE', 105, 15, { align: 'center' });
       
       doc.setFontSize(10);
       doc.setTextColor(100);
       doc.text('Premium Products & Services', 105, 22, { align: 'center' });
       
       // Add invoice title
       doc.setFontSize(16);
       doc.setTextColor(10, 42, 102);
       doc.text('INVOICE', 105, 32, { align: 'center' });
       
       // Order details section
       doc.setFontSize(11);
       doc.setTextColor(50);
       doc.text('Order Details:', 20, 42);
       
       doc.setFontSize(9);
       doc.text(`Order Number: ${order.orderNumber}`, 20, 48);
       doc.text(`Order Date: ${new Date(order.createdAt).toLocaleDateString()}`, 20, 54);
       doc.text(`Status: ${getStatusText(order.status)}`, 20, 60);
       doc.text(`Payment Method: ${order.paymentMethod}`, 20, 66);
                       doc.text(`Payment Status: Paid`, 20, 72);
         if (order.paymentId) {
           doc.text(`Payment Confirmation: ${order.paymentId}`, 20, 78);
           // Add one line space after payment confirmation
           doc.text(``, 20, 84);
         }
       
               // Customer details
        doc.setFontSize(11);
        doc.setTextColor(50);
        doc.text('Customer Details:', 20, 82);
        
                 doc.setFontSize(9);
         doc.text(`Name: ${order.customerName}`, 20, 88);
         doc.text(`Phone: ${order.customerPhone}`, 20, 94);
         doc.text(`Email: ${order.customerEmail}`, 20, 100);
         doc.text(`Address: ${formatShippingAddress(order.shippingAddress)}`, 20, 106);
       
       // Store details
       doc.setFontSize(11);
       doc.setTextColor(50);
       doc.text('Store Details:', 120, 82);
       
       doc.setFontSize(9);
       doc.text('Nistylez Store', 120, 88);
       doc.text('Official Store • Premium Products', 120, 94);
       doc.text('Email: nistylezaustralia@gmail.com', 120, 100);
       doc.text('Location: Adelaide, South Australia', 120, 106);
       
       // Delivery information
       if (order.estimatedDelivery || order.actualDelivery) {
         doc.setFontSize(11);
         doc.setTextColor(50);
         doc.text('Delivery Information:', 20, 122);
         
         doc.setFontSize(9);
         if (order.estimatedDelivery) {
           doc.text(`Expected Delivery: ${new Date(order.estimatedDelivery).toLocaleDateString()}`, 20, 128);
         }
         if (order.actualDelivery) {
           doc.text(`Actual Delivery: ${new Date(order.actualDelivery).toLocaleDateString()}`, 20, 134);
         }
         if (order.trackingNumber) {
           doc.text(`Tracking Number: ${order.trackingNumber}`, 20, 140);
         }
         if (order.courierName) {
           doc.text(`Courier: ${order.courierName}`, 20, 146);
         }
       }
       
              // Items section (without autoTable)
       const startY = order.actualDelivery ? 160 : 150;
       
       doc.setFontSize(11);
       doc.setTextColor(50);
       doc.text('Order Items:', 20, startY);
       
       let currentY = startY + 8;
       
       // Draw table header manually
       doc.setFillColor(10, 42, 102);
       doc.rect(20, currentY - 4, 170, 6, 'F');
       doc.setTextColor(255);
       doc.setFontSize(8);
       doc.text('Product', 25, currentY);
       doc.text('Qty', 100, currentY);
       doc.text('Price', 130, currentY);
       doc.text('Total', 170, currentY);
       
       currentY += 8;
       doc.setTextColor(50);
       
       // Add items
       order.items.forEach((item, index) => {
         if (currentY > 240) {
           doc.addPage();
           currentY = 20;
         }
         
         doc.setFontSize(8);
         doc.text(item.productName.substring(0, 25), 25, currentY);
         doc.text(item.quantity.toString(), 100, currentY);
         doc.text(`$${item.price.toFixed(2)}`, 130, currentY);
         doc.text(`$${item.total.toFixed(2)}`, 170, currentY);
         
         if (item.discount > 0) {
           doc.setFontSize(6);
           doc.setTextColor(76, 175, 80);
           doc.text(`Save $${item.discount.toFixed(2)}`, 25, currentY + 3);
           doc.setTextColor(50);
         }
         
         currentY += 6;
         
         if (index < order.items.length - 1) {
           doc.setDrawColor(200);
           doc.line(20, currentY, 190, currentY);
           currentY += 2;
         }
       });
       
       // Order summary
       currentY += 8;
       doc.setFontSize(11);
       doc.setTextColor(50);
       doc.text('Order Summary:', 20, currentY);
       
       currentY += 8;
       doc.setFontSize(9);
       doc.text('Subtotal:', 120, currentY);
               doc.text(`$${order.subtotal.toFixed(2)}`, 180, currentY, { align: 'right' });
       
       if (order.totalDiscount > 0) {
         currentY += 6;
         doc.setTextColor(76, 175, 80); // Green for discount
         doc.text('Discount:', 120, currentY);
         doc.text(`-$${order.totalDiscount.toFixed(2)}`, 180, currentY, { align: 'right' });
         doc.setTextColor(50);
       }

       // Coupon Discount
       if (order.couponDiscount > 0) {
         currentY += 6;
         doc.setTextColor(76, 175, 80); // Green for discount
         doc.text(`Coupon Discount${order.appliedCoupon ? ` (${order.appliedCoupon})` : ''}:`, 120, currentY);
         doc.text(`-$${order.couponDiscount.toFixed(2)}`, 180, currentY, { align: 'right' });
         doc.setTextColor(50);
       }

       // Wallet Balance Applied
       if (order.walletBalanceUsed > 0) {
         currentY += 6;
         doc.setTextColor(76, 175, 80); // Green for discount
         doc.text('Wallet Applied:', 120, currentY);
         doc.text(`-$${order.walletBalanceUsed.toFixed(2)}`, 180, currentY, { align: 'right' });
         doc.setTextColor(50);
       }

       // Gems Applied
       if (order.gemsUsed > 0) {
         currentY += 6;
         doc.setTextColor(76, 175, 80); // Green for discount
         doc.text('Gems Applied:', 120, currentY);
         doc.text(`-$${(order.gemsUsed * 10).toFixed(2)}`, 180, currentY, { align: 'right' });
         doc.setTextColor(50);
       }
       
       currentY += 6;
       doc.text('Shipping Fee:', 120, currentY);
       doc.text(`$${order.shipping.toFixed(2)}`, 180, currentY, { align: 'right' });
       

       
       // Total
       currentY += 8;
       doc.setFontSize(12);
       doc.setTextColor(10, 42, 102);
       doc.setFont(undefined, 'bold');
       doc.text('Total:', 120, currentY);
       doc.text(`$${order.totalAmount.toFixed(2)}`, 180, currentY, { align: 'right' });
       doc.setFont(undefined, 'normal');
       
       // Footer
       currentY += 15;
       doc.setFontSize(7);
       doc.setTextColor(100);
       doc.text('Thank you for your purchase!', 105, currentY, { align: 'center' });
       doc.text('For any queries, contact us at support@nistylez.com', 105, currentY + 5, { align: 'center' });
       doc.text('Generated on: ' + new Date().toLocaleString(), 105, currentY + 10, { align: 'center' });
      
      // Save the PDF
      doc.save(`Order_${order.orderNumber}_Invoice.pdf`);
      
    } catch (error) {
      alert('Error generating PDF. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    // Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: `Order ${order?.orderNumber}`,
        text: `Check out my order from Nistylez Store`,
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleTrackOrder = () => {
    if (order?.trackingUrl) {
      window.open(order.trackingUrl, '_blank');
    } else if (order?.trackingNumber) {
      alert(`Tracking Number: ${order.trackingNumber}`);
    } else {
      alert('Tracking information is not available yet');
    }
  };

  if (loading) {
    return (
      <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ 
          backgroundColor: '#0A2A66', 
          color: 'white', 
          p: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Order Details
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <BrandLoader />
        </Box>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ 
          backgroundColor: '#0A2A66', 
          color: 'white', 
          p: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Order Details
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2 
        }}>
          <Alert severity="error">
            <AlertTitle>Order Not Found</AlertTitle>
            The order you're looking for doesn't exist.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: 2,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Order #{order.orderNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download PDF">
              <IconButton onClick={handleDownloadPDF} sx={{ color: 'white' }}>
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint} sx={{ color: 'white' }}>
                <Print />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton onClick={handleShare} sx={{ color: 'white' }}>
                <Email />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2 
        }}>
        {/* Order Status Card */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ fontSize: 18, color: '#007AFF' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  #{order.orderNumber}
                </Typography>
              </Box>
              <Chip
                label={getStatusText(order.status)}
                sx={{
                  backgroundColor: getStatusColor(order.status),
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            
            {/* Combined Order Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="caption" color="text.secondary">
              {new Date(order.createdAt).toLocaleDateString()}
            </Typography>
              </Box>
              <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ShoppingBag sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  {order.items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0} item{(order.items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0) > 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Payment sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  Paid
                </Typography>
              </Box>
              {order.trackingNumber && (
                <>
                  <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalShipping sx={{ fontSize: 14, color: '#007AFF' }} />
                    <Typography variant="caption" sx={{ color: '#007AFF' }}>
                      Tracked
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Provider Details */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar sx={{ bgcolor: '#007AFF', width: 40, height: 40 }}>
                <Store />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                    Nistylez Store
                  </Typography>
                  <Verified sx={{ fontSize: 16, color: '#4CAF50' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Official Store • Premium Products
                </Typography>
              </Box>
            </Box>
            
            {/* Combined Contact Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  nistylezaustralia@gmail.com
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  Adelaide, South Australia
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Order Items
            </Typography>
            <List sx={{ p: 0 }}>
              {order.items.map((item, index) => (
                <Box key={item._id}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemAvatar>
                      <Avatar 
                        src={item.productImage} 
                        variant="rounded"
                        sx={{ width: 50, height: 50 }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', flex: 1 }}>
                            {item.productName}
                          </Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#007AFF' }}>
                              ${item.price.toFixed(2)}
                            </Typography>
                            {item.discount > 0 && (
                              <Typography variant="caption" sx={{ color: '#666', textDecoration: 'line-through' }}>
                                ${item.originalPrice.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {item.quantity}
                            </Typography>
                            {item.discount > 0 && (
                              <>
                                <Box sx={{ width: '1px', height: 8, backgroundColor: '#e0e0e0' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TrendingDown sx={{ fontSize: 12, color: '#4CAF50' }} />
                                  <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                                    Save ${item.discount.toFixed(2)}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            ${item.total.toFixed(2)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < order.items.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Order Summary
            </Typography>
            <Box sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                                 <Typography variant="body2">${order.subtotal.toFixed(2)}</Typography>
              </Box>
              {order.totalDiscount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalOffer sx={{ fontSize: 14, color: '#4CAF50' }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>Discount:</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4CAF50' }}>-${order.totalDiscount.toFixed(2)}</Typography>
                </Box>
              )}

              {/* Coupon Discount */}
              {(order.couponDiscount > 0 || order.appliedCoupon) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalOffer sx={{ fontSize: 14, color: '#4CAF50' }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      🎉 Coupon Discount {order.appliedCoupon ? `(${order.appliedCoupon})` : ''}:
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                    -${(order.couponDiscount || 0).toFixed(2)}
                  </Typography>
                </Box>
              )}

              {/* Wallet Balance Applied */}
              {order.walletBalanceUsed > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Payment sx={{ fontSize: 14, color: '#4CAF50' }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>💳 Wallet Applied:</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                    -${order.walletBalanceUsed.toFixed(2)}
                  </Typography>
                </Box>
              )}

              {/* Gems Applied */}
              {order.gemsUsed > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Star sx={{ fontSize: 14, color: '#4CAF50' }} />
                    <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                      💎 {order.gemsUsed} Gems Applied:
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                    -${((order.gemsUsed / 1000) * 10).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Shipping:</Typography>
                                 <Typography variant="body2">${order.shipping.toFixed(2)}</Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Total:
                </Typography>
                                 <Typography variant="h6" sx={{ fontWeight: 700, color: '#007AFF' }}>
                   ${order.totalAmount.toFixed(2)}
                 </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Delivery Information */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Delivery Information
            </Typography>
            
            {/* Combined Delivery Dates */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  Ordered: {new Date(order.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 14, color: '#666' }} />
                <Typography variant="caption" color="text.secondary">
                  Expected: {new Date(order.estimatedDelivery).toLocaleDateString()}
                </Typography>
              </Box>
              {order.actualDelivery && (
                <>
                  <Box sx={{ width: '1px', height: 12, backgroundColor: '#e0e0e0' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 14, color: '#4CAF50' }} />
                    <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                      Delivered: {new Date(order.actualDelivery).toLocaleDateString()}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Shipping Address */}
            <Box sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn sx={{ fontSize: 16, color: '#007AFF' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                  Shipping Address
                </Typography>
              </Box>
                             <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                 {order.customerName}
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                 {order.customerPhone}
               </Typography>
                                             <Typography variant="body2" color="text.secondary">
                  {formatShippingAddress(order.shippingAddress)}
                </Typography>
            </Box>

                         {/* Shipping Information */}
             <Box sx={{ mt: 2, backgroundColor: '#f8f9fa', p: 2, borderRadius: 1 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                 <LocalShipping sx={{ fontSize: 16, color: '#007AFF' }} />
                 <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                   Shipping Information
                 </Typography>
               </Box>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                   <Business sx={{ fontSize: 14, color: '#666' }} />
                                       <Typography variant="body2" color="text.secondary">
                      Courier: {order.courierName}
                    </Typography>
                 </Box>
                 {order.trackingNumber && (
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <Receipt sx={{ fontSize: 14, color: '#666' }} />
                     <Typography variant="body2" color="text.secondary">
                       Tracking Number: {order.trackingNumber}
                     </Typography>
                   </Box>
                 )}
                 {order.trackingUrl && (
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <LocalShipping sx={{ fontSize: 14, color: '#666' }} />
                     <Typography variant="body2" color="text.secondary">
                       Tracking URL: {order.trackingUrl}
                     </Typography>
                   </Box>
                 )}
                 {order.trackingNumber && (
                   <Button
                     variant="outlined"
                     size="small"
                     startIcon={<LocalShipping />}
                     onClick={handleTrackOrder}
                     sx={{ mt: 1, borderRadius: 1, alignSelf: 'flex-start' }}
                   >
                     Track Package
                   </Button>
                 )}
               </Box>
             </Box>
          </CardContent>
        </Card>

        {/* Payment Information */}
        <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Payment Information
            </Typography>
            <Box sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment sx={{ fontSize: 16, color: '#007AFF' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {order.paymentMethod}
                  </Typography>
                </Box>
                <Chip
                  label="Paid"
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
                             {order.paymentId && (
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                   <Receipt sx={{ fontSize: 14, color: '#666' }} />
                   <Typography variant="body2" color="text.secondary">
                     Confirmation: {order.paymentId}
                   </Typography>
                 </Box>
               )}
            </Box>
          </CardContent>
        </Card>

        {/* Notes */}
        {order.notes && (
          <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Info sx={{ fontSize: 16, color: '#007AFF' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                  Notes
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {order.notes}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            Download Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default ProductOrderDetails; 